import { useEffect, useRef } from 'react';
import { StockChartRenderer } from '../webgpu/stock-chart-renderer';
import { WebGLStockChartFallback } from '../webgpu/webgl-stock-chart-fallback';

export const Background3D: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<{
    renderer: StockChartRenderer | WebGLStockChartFallback;
    cleanup: () => void;
  } | null>(null);
  const initializingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log('No canvas element');
      return;
    }

    // Prevent multiple simultaneous initializations
    if (initializingRef.current || rendererRef.current) {
      console.log('Already initializing or renderer exists, skipping');
      return;
    }

    initializingRef.current = true;

    console.log('Initializing Background3D renderer');

    // Set canvas size
    const resizeCanvas = () => {
      if (canvasRef.current) {
        canvasRef.current.width = window.innerWidth;
        canvasRef.current.height = window.innerHeight;
      }
    };
    resizeCanvas();

    const handleError = (error: string) => {
      console.error('Renderer error:', error);
    };

    // Initialize renderer
    const initializeRenderer = async () => {
      const initWebGLFallback = async () => {
        console.log('Initializing WebGL fallback');
        const webglFallback = new WebGLStockChartFallback(handleError);
        const success = await webglFallback.initialize(canvas);
        if (success) {
          rendererRef.current = {
            renderer: webglFallback,
            cleanup: () => {
              webglFallback.cleanup();
              rendererRef.current = null;
            },
          };
        }
      };

      const stockChartRenderer = new StockChartRenderer(handleError, initWebGLFallback);
      const success = await stockChartRenderer.initialize(canvas);

      if (success) {
        console.log('Stock chart renderer initialized successfully');
        rendererRef.current = {
          renderer: stockChartRenderer,
          cleanup: () => {
            console.log('Cleaning up stock chart renderer');
            stockChartRenderer.cleanup();
            rendererRef.current = null;
          },
        };
      } else {
        console.error('Stock chart renderer failed, falling back to WebGL');
        // The fallback is called by the `stockChartRenderer` internally
        // via the `initWebGLFallback` callback passed to the constructor.
      }
    };

    initializeRenderer();

    const handleResize = () => {
      resizeCanvas();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      console.log('Background3D cleanup');
      window.removeEventListener('resize', handleResize);
      if (rendererRef.current) {
        rendererRef.current.cleanup();
        rendererRef.current = null;
      }
      initializingRef.current = false;
    };
  }, []); // Run once on mount

  return (
    <>
      <canvas
        ref={canvasRef}
        className="background-canvas"
      />
    </>
  );
};
